
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, Target, TrendingUp, TrendingDown, AlertTriangle, Star } from "lucide-react";
import { AIRecommendationResult } from '@/types/ai-analysis';
import { useTranslation } from 'react-i18next';

interface EnhancedAiRecommendationCardProps {
  data: AIRecommendationResult;
  currentPrice?: number;
}

export const EnhancedAiRecommendationCard = ({ data, currentPrice }: EnhancedAiRecommendationCardProps) => {
  const { t } = useTranslation();
  // Ensure data has required structure
  const validatedData = {
    action: data.action || 'HOLD',
    confidence: data.confidence || 50,
    timeframe: data.timeframe || 'Medium',
    reasoning: data.reasoning || 'Không có thông tin phân tích',
    riskLevel: data.riskLevel || 'medium',
    warnings: data.warnings || ['Không có cảnh báo'],
    detailedAnalysis: {
      technical: data.detailedAnalysis?.technical || 'Không có phân tích kỹ thuật',
      fundamental: data.detailedAnalysis?.fundamental || 'Không có phân tích cơ bản',
      marketSentiment: data.detailedAnalysis?.marketSentiment || 'Không có phân tích tâm lý thị trường',
      forecast: data.detailedAnalysis?.forecast || 'Không có dự báo'
    },
    qualityScore: data.qualityScore
  };

  const getActionColor = (action: 'BUY' | 'SELL' | 'HOLD') => {
    switch(action) {
      case 'BUY': return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'SELL': return 'bg-red-500/20 text-red-700 border-red-500/30';
      case 'HOLD': return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
    }
  };

  const getRiskColor = (level: 'low' | 'medium' | 'high') => {
    switch(level) {
      case 'low': return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      case 'high': return 'bg-red-500/20 text-red-700 border-red-500/30';
    }
  };

  const getActionIcon = (action: 'BUY' | 'SELL' | 'HOLD') => {
    switch(action) {
      case 'BUY': return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'SELL': return <TrendingDown className="h-5 w-5 text-red-500" />;
      case 'HOLD': return <Target className="h-5 w-5 text-yellow-500" />;
    }
  };

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-blue-500/5 border border-border/50 hover:shadow-lg transition-all duration-300">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Brain className="h-5 w-5 text-blue-500" />
          {t('market_analysis.enhanced_ai_recommendation')}
          {validatedData.qualityScore && (
            <div className="flex items-center gap-1 ml-auto">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-muted-foreground">{validatedData.qualityScore}/100</span>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Action & Confidence */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-accent/30 via-accent/20 to-transparent rounded-xl">
          <div className="flex items-center gap-3">
            {getActionIcon(validatedData.action as 'BUY' | 'SELL' | 'HOLD')}
            <div>
              <Badge className={`${getActionColor(validatedData.action as 'BUY' | 'SELL' | 'HOLD')} text-lg px-3 py-1`}>
                {validatedData.action}
              </Badge>
              <div className="text-sm text-muted-foreground mt-1">
                {t('market_analysis.timeframe')}: {validatedData.timeframe}
              </div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-1">{t('market_analysis.confidence')}</div>
            <div className="text-xl font-bold text-blue-500 flex items-center gap-2">
              <span>{validatedData.confidence}%</span>
              <div className="w-12 bg-muted/50 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-400"
                  style={{ width: `${validatedData.confidence}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Risk Level */}
        <div className="p-4 bg-gradient-to-br from-accent/30 via-accent/20 to-transparent rounded-lg">
          <div className="text-sm text-muted-foreground mb-2">{t('market_analysis.risk_level')}</div>
          <Badge className={getRiskColor(validatedData.riskLevel as 'low' | 'medium' | 'high')}>
            {validatedData.riskLevel.toUpperCase()}
          </Badge>
        </div>

        {/* Detailed Analysis */}
        <div className="space-y-4">
          <div className="p-6 bg-gradient-to-br from-accent/30 via-accent/20 to-transparent rounded-xl">
            <div className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Brain className="h-5 w-5 text-blue-500" />
              {t('market_analysis.detailed_ai_analysis')}
            </div>
            
            {/* Technical Analysis */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-blue-400 mb-2 uppercase tracking-wide">{t('market_analysis.technical_analysis')}</h4>
              <p className="text-sm text-foreground leading-relaxed">
                {validatedData.detailedAnalysis.technical}
              </p>
            </div>

            {/* Fundamental Analysis */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-emerald-400 mb-2 uppercase tracking-wide">{t('market_analysis.fundamental_analysis')}</h4>
              <p className="text-sm text-foreground leading-relaxed">
                {validatedData.detailedAnalysis.fundamental}
              </p>
            </div>

            {/* Market Sentiment */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-purple-400 mb-2 uppercase tracking-wide">{t('market_analysis.market_sentiment')}</h4>
              <p className="text-sm text-foreground leading-relaxed">
                {validatedData.detailedAnalysis.marketSentiment}
              </p>
            </div>

            {/* Forecast */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-amber-400 mb-2 uppercase tracking-wide">{t('market_analysis.forecast')}</h4>
              <p className="text-sm text-foreground leading-relaxed">
                {validatedData.detailedAnalysis.forecast}
              </p>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {validatedData.warnings.length > 0 && (
          <div className="p-4 bg-gradient-to-br from-yellow-500/10 via-yellow-500/5 to-transparent rounded-xl border border-yellow-500/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-700">{t('market_analysis.risk_warnings')}:</span>
            </div>
            <ul className="space-y-1">
              {validatedData.warnings.map((warning, index) => (
                <li key={index} className="text-sm text-yellow-700 flex items-start gap-2">
                  <span className="w-1 h-1 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Main Reasoning */}
        <div className="p-4 bg-gradient-to-br from-accent/30 via-accent/20 to-transparent rounded-lg">
          <div className="text-sm text-muted-foreground mb-2">{t('market_analysis.ai_reasoning')}</div>
          <p className="text-sm text-foreground leading-relaxed">
            {validatedData.reasoning}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
